




import * as d3 from "d3";
import { addDays } from "date-fns";
import crossfilter, { Group } from "crossfilter2";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import { Record } from "@/types";

@Component({})
export default class TimelineSlider extends Vue {
  @Prop()
  private records!: Record[];

  @Prop()
  private height!: number;

  padding = { top: 80, bot: 20, left: 30, right: 15 };

  mounted() {
    this.$nextTick(this.draw);
  }

  get recordsByDate() {
    return crossfilter(this.records).dimension(d => d.date);
  }

  get groupedDates() {
    return this.recordsByDate.group();
  }

  get hScale() {
    return d3
      .scaleLinear()
      .domain([0, this.groupedDates.top(1)[0].value as number])
      .range([0, this.height - this.padding.bot]);
  }

  @Watch("xScale")
  @Watch("yScale")
  @Watch("records")
  public draw() {
    if (
      !this.$el ||
      !this.records ||
      !this.height ||
      !this.padding ||
      !this.hScale
    ) {
      return;
    }

    const recordsByDate = crossfilter(this.records).dimension(
      record => record.date
    );

    const groupedDates: Group<Record, Date, number> = recordsByDate.group();

    const xScale = d3
      .scaleLinear()
      .domain([
        new Date(this.recordsByDate.bottom(1)[0].date),
        addDays(new Date(this.recordsByDate.top(1)[0].date), 1)
      ])
      .range([
        this.padding.left + 10,
        this.$el.clientWidth - this.padding.right
      ]);

    const yScale = d3
      .scaleLinear()
      .domain([0, this.groupedDates.top(1)[0].value as number])
      .range([this.height - this.padding.bot, 0]);

    const canvas = d3
      .select(this.$el)
      .html("")
      .append("svg")
      .attr("width", this.$el.clientWidth)
      .attr("height", this.height)
      .style("overflow", "visible");

    canvas
      .append("g")
      .attr("transform", `translate(0, ${this.height - this.padding.bot})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickFormat(d3.timeFormat("%Y %b") as any)
          .ticks(this.$el.clientWidth / 60)
      );

    canvas
      .append("g")
      .attr("class", "grid")
      .attr("transform", "translate(" + this.padding.left + ", 0)")
      .call(
        d3
          .axisLeft(yScale)
          .ticks(3)
          .tickSize(
            -this.$el.clientWidth + this.padding.left + this.padding.right
          )
      );

    canvas
      .selectAll(".date")
      .data(groupedDates.top(Infinity))
      .enter()
      .append("rect")
      .attr("x", d => xScale && xScale(d.key) - 10)
      .attr("y", d => yScale && yScale(d.value))
      .attr("width", 10)
      .attr("height", d => this.hScale && this.hScale(d.value));

    const brush = d3
      .brushX()
      .extent([
        [this.padding.left, 0],
        [
          this.$el.clientWidth - this.padding.right,
          this.height - this.padding.bot
        ]
      ])
      .on("brush end", () => {
        this.$emit(
          "pan",
          d3.event.selection
            .map(xScale.invert, xScale)
            .map((e: number) => new Date(e))
        );
      });

    canvas
      .append("g")
      .attr("class", "brush")
      .call(brush)
      .call(brush.move, [
        xScale(new Date(recordsByDate.bottom(1)[0].date)) - 10,
        xScale(new Date(recordsByDate.top(1)[0].date)) + 1
      ]);
  }
}
